//------------------------------------------------------------------------------
// name: CitySelector.tsx
// author: mudas
// created: 2025/12/06
//------------------------------------------------------------------------------

import React, { FC } from 'react';
import { useQuery } from '@tanstack/react-query';

import { useProxyValue } from './useProxyValue';
import { selectFilterOption } from 'common/helper/search';

import { Identity } from 'common/types';
import { SimpleValueType } from './types';
import type { ProxyValue } from './useProxyValue';
import service from 'common/service';

import { Select, SelectProps } from '@fuxi/eevee-ui';

type ValueType = SimpleValueType;
type OptionType = Identity;

export interface IProps extends Omit<SelectProps<ValueType, OptionType>, 'onChange' | 'value'> {
  provinceId?: number;

  // 是否返回 option 替代 value
  replaceAsOption?: boolean;

  // 受 replaceAsOption 影响，value 为 option 时，onChange 回调参数为 option，否则为 value
  value?: ProxyValue<OptionType>;
  onChange?: (value: ProxyValue<OptionType>) => void;
}

const CitySelector: FC<IProps> = (props) => {
  const { provinceId, replaceAsOption, value: baseValue, onChange: baseOnChange, ...otherProps } = props;

  const { selectValue, onChange } = useProxyValue<OptionType>({
    fieldName: 'id',
    replaceAsOption,
    value: baseValue,
    onChange: baseOnChange,
  });

  const { data, isFetching } = useQuery({
    queryKey: ['getSystemCity', provinceId],
    queryFn: async () => {
      const res = await service.system.getSystemCity(provinceId!);
      return res.data;
    },
    enabled: !!provinceId,
    cacheTime: 10 * 60 * 1000,
    staleTime: 10 * 60 * 1000,
  });

  return (
    <Select<ValueType, OptionType>
      loading={isFetching}
      options={data}
      value={selectValue}
      showSearch
      allowClear
      placeholder="请选择城市"
      fieldNames={{ label: 'name', value: 'id' }}
      filterOption={selectFilterOption({ label: 'name', value: 'id' })}
      onChange={onChange}
      {...otherProps}
    />
  );
};

export default CitySelector;
